import {
  ArrowRight,
  Building2,
  Check,
  ClipboardList,
  HeartPulse,
  Hospital,
  Mail,
  MapPin,
  Menu,
  Phone,
  ShieldCheck,
  Stethoscope,
  Utensils,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

const clinicalAreas = [
  {
    title: "Cancer nutrition",
    text: "Practical nutrition support during treatment, around surgery and throughout recovery and survivorship.",
  },
  {
    title: "Critical care & rehabilitation",
    text: "Hospital-based nutrition support for ICU care, step-down care, rehabilitation and recovery.",
  },
  {
    title: "Renal nutrition",
    text: "Individualised dietary guidance for kidney-related conditions, aligned with clinical needs and medical care.",
  },
  {
    title: "Gastrointestinal health",
    text: "Evidence-based nutrition strategies for digestive health, symptoms and appropriate nutrition support.",
  },
  {
    title: "Mental health & eating disorders",
    text: "Compassionate nutrition care that considers mental health, eating behaviour and multidisciplinary support.",
  },
  {
    title: "Diabetes & weight management",
    text: "A realistic, mindful approach focused on sustainable habits rather than restrictive or one-size-fits-all plans.",
  },
];

const supportedConditions = [
  {
    category: "Metabolic & cardiovascular",
    items: [
      "Metabolic syndrome",
      "Hypertension",
      "Cardiovascular disease",
      "Dyslipidaemia",
      "Arthritis & gout",
      "Prediabetes & insulin resistance",
      "Diabetes",
      "PCOS",
      "Underweight",
      "Overweight & obesity",
    ],
  },
  {
    category: "Gastrointestinal & food-related",
    items: [
      "Constipation",
      "Diarrhoea",
      "Gastrointestinal surgery",
      "Crohn’s disease",
      "Diverticular disease",
      "GORD",
      "Irritable bowel syndrome",
      "Hepatitis",
      "Peptic ulcer disease",
      "Pancreatitis",
      "Food allergy & intolerance",
      "Chewing & swallowing difficulties",
    ],
  },
  {
    category: "Complex clinical nutrition",
    items: [
      "HIV, TB & immunocompromised nutrition support",
      "Cancer",
      "Renal failure",
      "Enteral feeding",
      "Malnutrition",
      "Nutrient & micronutrient deficiencies",
    ],
  },
  {
    category: "Family & life stages",
    items: [
      "Malnourished children & fussy eating",
      "Eating disorders",
      "Pregnancy",
    ],
  },
];

const consultingServices = [
  "Menu design, nutritional analysis and special-diet planning",
  "Hospital and institutional kitchen consulting",
  "Hostel menu development and staff training",
  "Food-safety training and workplace wellness initiatives",
];

const facilities = [
  "Mediclinic Windhoek",
  "Namibian Oncology Centre",
  "Paramount",
  "Treeside Medical Suites",
  "Westcare Step Down",
  "Ombili Mental Health",
];

function Header() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);

  return (
    <header className="site-header">
      <a className="site-brand" href="#top" aria-label="Juliane Courtney-Clarke home">
        <span>
          <strong>Juliane Courtney-Clarke</strong>
          <small>Registered Dietitian</small>
        </span>
      </a>
      <button
        className="menu-button"
        type="button"
        aria-label={open ? "Close navigation" : "Open navigation"}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? <X /> : <Menu />}
      </button>
      <nav className={open ? "site-nav is-open" : "site-nav"} aria-label="Primary navigation">
        <a href="#about">About</a>
        <a href="#clinical-care">Clinical care</a>
        <a href="#consulting">Consulting</a>
        <a href="#contact" className="nav-cta">Request an appointment</a>
      </nav>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top" aria-labelledby="hero-title">
      <div className="hero__content">
        <p className="eyebrow"><span /> Registered dietitian · Windhoek, Namibia</p>
        <h1 id="hero-title">Clinical nutrition, grounded in evidence and <em>centred on you.</em></h1>
        <p className="hero__intro">
          Personalised nutrition care for individuals, families and healthcare facilities—delivered with clarity,
          compassion and respect for your medical needs and everyday life.
        </p>
        <div className="hero__actions">
          <a className="button button--primary" href="tel:+264817502484">
            <Phone size={17} aria-hidden="true" /> Call for an appointment
          </a>
          <a className="text-link" href="#clinical-care">
            Explore clinical services <ArrowRight size={16} aria-hidden="true" />
          </a>
        </div>
        <div className="trust-row" aria-label="Practice details">
          <span><ShieldCheck size={17} /> Evidence-based care</span>
          <span><Hospital size={17} /> Hospital consultations</span>
          <span><ClipboardList size={17} /> Major medical aids</span>
        </div>
      </div>

      <div className="hero__visual" aria-label="Juliane Courtney-Clarke practice identity">
        <div className="hero__wash hero__wash--one" />
        <div className="hero__wash hero__wash--two" />
        <div className="credential-card">
          <img src="/brand/logo-original-enhanced-web.png" alt="Juliane Courtney-Clarke, Registered Dietitian" />
          <dl>
            <div><dt>Qualification</dt><dd>BSc Dietetics</dd></div>
            <div><dt>Practice</dt><dd>Established in Windhoek</dd></div>
            <div><dt>Practice no.</dt><dd>084 000 0535109</dd></div>
          </dl>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="section section--about" id="about" aria-labelledby="about-title">
      <div className="section-label">01 · About</div>
      <div className="about-grid">
        <div>
          <p className="kicker">Professional guidance in a noisy nutrition landscape</p>
          <h2 id="about-title">Science-backed care with the person—not the trend—at the centre.</h2>
        </div>
        <div className="prose">
          <p>
            I’m Juliane Courtney-Clarke, a registered dietitian dedicated to evidence-based clinical nutrition.
            My role is to help you make informed, practical choices that support your health and quality of life.
          </p>
          <p>
            I completed my Bachelor of Science in Dietetics at Stellenbosch University in 2008. After spending
            time abroad, I established my private practice in Windhoek, where I now divide my time between
            private consultations and hospital-based nutrition care.
          </p>
          <p>
            Every person’s health, circumstances and relationship with food are different. My approach is
            therefore individualised, collaborative and grounded in current clinical evidence—never in pressure,
            judgement or quick fixes.
          </p>
        </div>
      </div>
      <div className="experience-band">
        <div><strong>Since 2008</strong><span>Clinical dietetics experience</span></div>
        <div><strong>Private + hospital</strong><span>Continuity across care settings</span></div>
        <div><strong>Multidisciplinary</strong><span>Collaboration with health professionals</span></div>
      </div>
    </section>
  );
}

function ClinicalCare() {
  return (
    <section className="section section--clinical" id="clinical-care" aria-labelledby="clinical-title">
      <div className="section-heading">
        <div>
          <div className="section-label">02 · Clinical nutrition</div>
          <h2 id="clinical-title">Specialised support through treatment, recovery and everyday health.</h2>
        </div>
        <p>
          These highlighted areas reflect Juliane’s particular clinical interests. Nutrition care is tailored to
          your diagnosis, treatment plan, symptoms, preferences and stage of life.
        </p>
      </div>
      <div className="clinical-grid">
        {clinicalAreas.map((area, index) => (
          <article className="clinical-card" key={area.title}>
            <span className="clinical-card__number">{String(index + 1).padStart(2, "0")}</span>
            <HeartPulse size={22} aria-hidden="true" />
            <h3>{area.title}</h3>
            <p>{area.text}</p>
          </article>
        ))}
      </div>
      <p className="clinical-note">
        Care may be coordinated with your doctor and broader healthcare team where appropriate.
      </p>
      <div className="conditions-panel" aria-labelledby="conditions-title">
        <div className="conditions-panel__intro">
          <p className="kicker">Comprehensive dietetic support</p>
          <h3 id="conditions-title">Conditions and nutrition needs supported</h3>
          <p>
            Juliane also provides individualised nutrition care across the following conditions and clinical needs.
          </p>
        </div>
        <div className="conditions-grid">
          {supportedConditions.map((group) => {
            const headingId = `condition-${group.category.replaceAll(" ", "-").replaceAll("&", "and").toLowerCase()}`;
            return (
              <section className="conditions-group" key={group.category} aria-labelledby={headingId}>
                <h4 id={headingId}>{group.category}</h4>
                <ul>
                  {group.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Consulting() {
  return (
    <section className="section section--consulting" id="consulting" aria-labelledby="consulting-title">
      <div className="consulting-panel">
        <div>
          <div className="section-label section-label--light">03 · Institutional consulting</div>
          <h2 id="consulting-title">Nutrition systems that work in real kitchens and care environments.</h2>
          <p>
            Consulting support for hospitals, hostels, mental-health facilities, workplaces and other institutions
            that need practical, nutritionally sound food-service solutions.
          </p>
        </div>
        <ul>
          {consultingServices.map((service) => (
            <li key={service}><Check size={17} aria-hidden="true" /> {service}</li>
          ))}
        </ul>
      </div>
      <div className="consulting-types" aria-label="Consulting categories">
        <div><Utensils aria-hidden="true" /><strong>Menu systems</strong><span>Design, analysis and special diets</span></div>
        <div><Building2 aria-hidden="true" /><strong>Institutional support</strong><span>Kitchen consulting and training</span></div>
        <div><Stethoscope aria-hidden="true" /><strong>Clinical environments</strong><span>Hospital and mental-health facilities</span></div>
      </div>
    </section>
  );
}

function CareNetwork() {
  return (
    <section className="section section--network" aria-labelledby="network-title">
      <div className="network-copy">
        <div className="section-label">04 · Care network</div>
        <h2 id="network-title">Nutrition support across private practice and healthcare facilities.</h2>
        <p>
          Juliane provides private and hospital-based consultations, including nutrition support at the following
          Windhoek facilities:
        </p>
      </div>
      <div className="facility-list">
        {facilities.map((facility) => (
          <div key={facility}><span />{facility}</div>
        ))}
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="contact-section" id="contact" aria-labelledby="contact-title">
      <div className="contact-copy">
        <p className="eyebrow eyebrow--light"><span /> Appointments & enquiries</p>
        <h2 id="contact-title">Let’s discuss the support you need.</h2>
        <p>
          Contact the practice to enquire about a private consultation, hospital-based support or institutional
          consulting.
        </p>
        <div className="medical-aid-callout">
          <ShieldCheck aria-hidden="true" />
          <div><strong>Medical aids</strong><span>Contracted with all major medical aids in Namibia. Please confirm the benefits and requirements of your specific plan before an appointment.</span></div>
        </div>
      </div>
      <address className="contact-card">
        <a href="tel:+264817502484"><Phone aria-hidden="true" /><span><small>Mobile</small>081 750 2484</span></a>
        <a href="tel:+26461254414"><Phone aria-hidden="true" /><span><small>Practice</small>061 254 414</span></a>
        <a href="mailto:juliane.dietitian@gmail.com"><Mail aria-hidden="true" /><span><small>Email</small>juliane.dietitian@gmail.com</span></a>
        <div><MapPin aria-hidden="true" /><span><small>Practice address</small>3B Hugo Hahn Street, Klein Windhoek, Windhoek, Namibia</span></div>
      </address>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <p>
        Website information is general and does not replace an individual nutrition assessment or medical care.
        For urgent medical concerns, contact an appropriate healthcare service.
      </p>
      <div className="footer-meta"><span>Practice no. 084 000 0535109</span><span>Windhoek, Namibia</span></div>
    </footer>
  );
}

function BrandOptions() {
  return (
    <main className="brand-board" id="main">
      <header className="brand-board__header">
        <div><p className="eyebrow"><span /> Identity review</p><h1>Two considered directions.</h1></div>
        <a className="button button--secondary" href="/">View website</a>
      </header>
      <section className="brand-option">
        <div className="brand-option__meta"><span>Option 01</span><h2>Refined original</h2><p>Preserves the fruit-inspired identity while rebuilding all lettering as crisp, high-contrast vector artwork.</p></div>
        <div className="brand-option__art"><img src="/brand/logo-refined-original.svg" alt="Refined original logo option" /></div>
      </section>
      <section className="brand-option">
        <div className="brand-option__meta"><span>Option 02</span><h2>Clinical contemporary</h2><p>A quieter medical identity using a plate, leaf and JC monogram—designed for signage, documents and digital use.</p></div>
        <div className="brand-option__art"><img src="/brand/logo-clinical-concept.svg" alt="Clinical contemporary logo option" /></div>
      </section>
      <section className="brand-option brand-option--source is-selected">
        <div className="brand-option__meta"><span>Selected for the website</span><h2>Enhanced prescription-pad artwork</h2><p>The supplied identity has been cropped at high resolution, carefully sharpened and retained as the approved website direction.</p></div>
        <div className="brand-option__art"><img src="/brand/logo-original-enhanced-web.png" alt="Enhanced crop of the original prescription-pad logo" /></div>
      </section>
    </main>
  );
}

function App() {
  if (window.location.pathname.startsWith("/brand-options")) return <BrandOptions />;

  return (
    <>
      <Header />
      <main id="main">
        <Hero />
        <About />
        <ClinicalCare />
        <Consulting />
        <CareNetwork />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

export default App;
